"use client";

import { useAuth } from "@pex/auth/client";
import { usePwaAuth } from "@pex/ui-web/hooks/use-pwa-auth";
import { Button } from "@pex/ui-web/primitives/button";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

interface GatewayClientProps {
  accountsUrl: string;
  returnTo: string;
}

/**
 * Handles the unauthenticated state for both browsers and PWAs.
 *
 * - Normal browser: performs a client-side redirect to the Accounts login page
 *   (same end result as the previous server-side redirect, one extra hop).
 * - PWA (iOS/Android): opens Accounts login in a popup so the PWA shell is
 *   never navigated away from the Bridge domain.
 */
export function GatewayClient({ returnTo, accountsUrl }: GatewayClientProps) {
  const { user, loading } = useAuth();
  const router = useRouter();

  const { isPwa, openAuthPopup, isPolling } = usePwaAuth(accountsUrl, {
    onAuth: () => router.replace(returnTo),
  });

  // If the user is already authenticated (e.g. session refreshed while on this
  // page), send them straight to their destination.
  useEffect(() => {
    if (!loading && user) {
      router.replace(returnTo);
    }
  }, [loading, user, returnTo, router]);

  // For non-PWA browsers, immediately redirect to the Accounts login page.
  // We do this client-side rather than in the middleware so PWAs never hit a
  // cross-origin server redirect that would abandon the shell.
  useEffect(() => {
    if (loading || user || isPwa) {
      return;
    }
    const loginUrl = new URL("/login", accountsUrl);
    loginUrl.searchParams.set("return_to", returnTo);
    window.location.href = loginUrl.toString();
  }, [loading, user, isPwa, accountsUrl, returnTo]);

  // Loading: wait for auth state and PWA detection
  if (loading) {
    return null;
  }

  // Already authenticated: render nothing while router.replace fires
  if (user) {
    return null;
  }

  // Non-PWA: render nothing while window.location.href fires
  if (!isPwa) {
    return null;
  }

  // PWA and unauthenticated: show the sign-in prompt
  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <div className="space-y-2">
          <h1 className="font-semibold text-2xl tracking-tight">
            Sign in to Bridge
          </h1>
          <p className="text-muted-foreground text-sm">
            Your session has expired or you&apos;re not signed in.
          </p>
        </div>
        <Button
          className="w-full"
          disabled={isPolling}
          onClick={openAuthPopup}
          size="lg"
        >
          {isPolling ? "Signing in…" : "Sign in"}
        </Button>
      </div>
    </div>
  );
}
